// src/App.js
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/contexts/AuthContext';
import { SecurityProvider } from './components/contexts/SecurityContext'; 
import Header from './components/parts/Header';
import Sidebar from './components/parts/Sidebar';
import Home from './components/Home';
import Documents from './components/Documents';
import Users from './components/Users';
import Logos from './components/Logos';
import Hosts from './components/Hosts';
import Groups from './components/Groups';
import Login from './components/parts/Login';
import ResetPassword from './components/parts/ResetPassword'; 
import Settings from './components/Settings';
import ActivityLog from './components/ActivityLog';
import DeviceManager from './components/DeviceManager'; 
import CategoryManager from './components/CategoryManager'; 
import RegisteredDevice from './components/RegisteredDevice'; 
import ContractTemplates from './components/ContractTemplates'; 
import EmailSettings from './components/EmailSettings'; 
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const userRole = user.role_key || user.role;

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    // Fallback logic if they try to access a forbidden route
    if (userRole === 'system_admin') return <Navigate to="/users" replace />;
    if (userRole === 'manager') return <Navigate to="/documents" replace />;
    return <Navigate to="/documents" replace />;
  }

  return children;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (user) {
    const userRole = user.role_key || user.role;

    // --- REDIRECT LOGIC BASED ON ROLE ---
    if (userRole === 'manager') {
      return <Navigate to="/documents" replace />;
    }
    if (userRole === 'system_admin') {
      return <Navigate to="/users" replace />;
    }
    // Admin (and others) go to Dashboard
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main Layout Component
const MainLayout = () => {
  return (
    <SecurityProvider> 
      <div className="app">
        <Header />
        <div className="app-body">
          <Sidebar />
          <main className="main-content">
            <Routes>
              
              <Route path="/" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Home />
                </ProtectedRoute>
              } />
              
              <Route path="/hosts" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Hosts />
                </ProtectedRoute>
              } />

              <Route path="/groups" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Groups />
                </ProtectedRoute>
              } />

              <Route path="/activity-log" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ActivityLog />
                </ProtectedRoute>
              } />

              <Route path="/categories" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <CategoryManager />
                </ProtectedRoute>
              } />

              <Route path="/user-sessions" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <DeviceManager />
                </ProtectedRoute>
              } />
              
              <Route path="/devices" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <RegisteredDevice />
                </ProtectedRoute>
              } />

              <Route path="/contract-template" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ContractTemplates />
                </ProtectedRoute>
              } />

              <Route path="/email-settings" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <EmailSettings />
                </ProtectedRoute>
              } />

              {/* ADDED system_admin here */}
              <Route path="/users" element={
                <ProtectedRoute allowedRoles={['admin', 'system_admin']}>
                  <Users />
                </ProtectedRoute>
              } />

              <Route path="/documents" element={<Documents />} />
              <Route path="/logos" element={<Logos />} /> 
              <Route path="/settings" element={<Settings />} /> 
              
              <Route path="*" element={<Navigate to="/documents" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </SecurityProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />

            <Route path="/reset-password" element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            } />
            
            <Route path="/*" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
